export interface Point {
  x: number
  y: number
}

interface LinkInput {
  // Note: only the rows that are visible in their list - the caller clips the rest
  children: Point[]
  parent: Point
  radius?: number
  spineX: number
}

export interface LinkShape {
  // Note: the arms that end on the spine with a T-junction - the corner arms are part of `path`
  arms: string[]
  dots: Point[]
  key: string
  origin: Point
  path: string
}

type Side = 'left' | 'right'

interface Arm {
  end: Point
  side: Side
}

const DEFAULT_RADIUS = 8
const TOLERANCE = 0.5

const round = (value: number) => Math.round(value * 10) / 10

// Note: moving along the spine, a turn towards the left arm is clockwise, towards the right arm counterclockwise
const getSweep = (side: Side) => (side === 'left' ? 1 : 0)
const getDirection = (side: Side) => (side === 'left' ? -1 : 1)

export const buildLinkShape = ({
  children,
  parent,
  radius = DEFAULT_RADIUS,
  spineX,
}: LinkInput): LinkShape | null => {
  if (children.length === 0) return null

  const arms: Arm[] = [
    { end: { x: round(parent.x), y: round(parent.y) }, side: 'left' },
    ...children.map((child) => ({
      end: { x: round(child.x), y: round(child.y) },
      side: 'right' as const,
    })),
  ]
  const dots = arms.slice(1).map(({ end }) => end)
  const origin = arms[0].end
  const x = round(spineX)

  const ys = arms.map(({ end }) => end.y)
  const top = Math.min(...ys)
  const bottom = Math.max(...ys)

  const toShape = (path: string, tArms: string[]): LinkShape => ({
    arms: tArms,
    dots,
    key: [path, ...tArms].join('|'),
    origin,
    path,
  })

  if (bottom - top < TOLERANCE) {
    return toShape(`M${origin.x} ${origin.y} H${dots[0].x}`, [])
  }

  const r = Math.min(radius, (bottom - top) / 2)
  const atTop = arms.filter(({ end }) => Math.abs(end.y - top) < TOLERANCE)
  const atBottom = arms.filter(({ end }) => Math.abs(end.y - bottom) < TOLERANCE)
  const topCorner = atTop.length === 1 ? atTop[0] : undefined
  const bottomCorner = atBottom.length === 1 ? atBottom[0] : undefined

  let path = ''

  if (topCorner) {
    const dir = getDirection(topCorner.side)
    path += `M${topCorner.end.x} ${top} H${x + dir * r} A${r} ${r} 0 0 ${getSweep(topCorner.side)} ${x} ${top + r}`
  } else {
    path += `M${x} ${top}`
  }

  path += ` V${bottomCorner ? bottom - r : bottom}`

  if (bottomCorner) {
    const dir = getDirection(bottomCorner.side)
    path += ` A${r} ${r} 0 0 ${getSweep(bottomCorner.side)} ${x + dir * r} ${bottom} H${bottomCorner.end.x}`
  }

  const tArms = arms
    .filter((arm) => arm !== topCorner && arm !== bottomCorner)
    .map(({ end }) => `M${x} ${end.y} H${end.x}`)

  return toShape(path, tArms)
}
